import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, X, CheckCheck, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Platform-admin notification bell. Only renders for admins. Polls the
 * backend every 60s for the unread count + latest 30 notifications.
 * Click the bell to open a dropdown with quick "Mark read" + "Mark all read".
 */
const NotificationBell = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const dropdownRef = useRef(null);

  const isAdmin = !!user?.is_admin;

  const load = async () => {
    if (!isAdmin) return;
    try {
      const res = await api.get('/admin/notifications?limit=15');
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isAdmin) return null;
  // Hide floating bell on full-screen chat/meeting pages so it never blocks the composer/controls
  const hideOnRoutes = ['/employee/chat', '/team/chat', '/team/meetings'];
  if (hideOnRoutes.some((p) => location.pathname.startsWith(p))) return null;

  const markRead = async (id) => {
    try {
      await api.post(`/admin/notifications/${id}/read`);
      load();
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      const res = await api.post('/admin/notifications/read-all');
      toast.success(`Marked ${res.data?.count || 0} as read`);
      load();
    } catch (err) {
      toast.error('Failed to mark all read');
    }
  };

  const openNotification = (n) => {
    if (!n.is_read) markRead(n.id);
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const relTime = (iso) => {
    if (!iso) return '';
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.round(diff/60)}m ago`;
    if (diff < 86400) return `${Math.round(diff/3600)}h ago`;
    return `${Math.round(diff/86400)}d ago`;
  };

  return (
    <div ref={dropdownRef} className="fixed z-40 bottom-6 right-24 md:top-4 md:bottom-auto md:right-4">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        data-testid="notification-bell-btn"
        className="relative w-11 h-11 rounded-full bg-white/95 backdrop-blur border border-slate-200 shadow-md hover:shadow-lg flex items-center justify-center text-slate-700 hover:bg-white transition-all"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center px-1 ring-2 ring-white"
            data-testid="notification-unread-count"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 md:mt-0 md:top-14 bottom-14 md:bottom-auto w-[min(340px,calc(100vw-2rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden max-h-[70vh] flex flex-col" data-testid="notification-panel">
          <div className="p-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-teal-500" />
              <h3 className="font-semibold text-slate-900 text-sm">Notifications</h3>
              {unread > 0 && <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded">{unread} new</span>}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAllRead} title="Mark all read" className="text-xs text-teal-600 hover:underline" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-4 h-4 inline mr-0.5" />
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-slate-100" aria-label="Close">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="notification-list">
            {items.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-sm">You&apos;re all caught up 🎉</div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openNotification(n)}
                  className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-start gap-2 ${n.is_read ? 'opacity-70' : ''}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {n.severity === 'warning'
                      ? <AlertTriangle className="w-4 h-4 text-amber-500" />
                      : <Info className="w-4 h-4 text-teal-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${n.is_read ? 'text-slate-600' : 'text-slate-900 font-medium'}`}>{n.title}</p>
                    <p className="text-xs text-slate-500 line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{relTime(n.created_at)}</p>
                  </div>
                  {!n.is_read && <span className="w-2 h-2 rounded-full bg-rose-500 mt-2 flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
